import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useMemo } from 'react'
import {
    Body,
    ButtonAction,
    ButtonsContainer,
    ContentContainer,
    dialogPanel,
    dialogTitle
} from './styles'
import { IoCloseSharp } from 'react-icons/io5'
import profile from '../../../assets/svg/profile.svg'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { AuthenticationReducer } from 'types/autentication'
import { getRoleName } from 'utils/getRole'

// Spinner simples para indicar carregamento
const Spinner = () => (
    <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
)

interface GroupAssumeTreatmentModalProps {
    isOpen: boolean
    handleCloseModal: () => void
    handleAssumeTreatment: () => void
    isLoading?: boolean
}

const GroupAssumeTreatmentModal: React.FC<GroupAssumeTreatmentModalProps> = ({
    isOpen,
    handleCloseModal,
    handleAssumeTreatment,
    isLoading = false
}) => {
    const authenticationSelector: AuthenticationReducer = useSelector(
        (state: RootReducerState) => state.authenticationReducers
    )
    
    const profileStatus = useMemo(() => {
        if (authenticationSelector?.dataJwt?.profile) {
            return getRoleName(authenticationSelector?.dataJwt?.profile)
        }
    }, [authenticationSelector])

    return (
        <>
            <Transition
                appear
                show={isOpen}
                as={Fragment}
                data-testid="test-GroupAssumeTreatment"
            >
                <Dialog
                    as="div"
                    className="relative z-10"
                    onClose={handleCloseModal}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className={dialogPanel}>
                                    <Dialog.Title
                                        as="h3"
                                        className={dialogTitle}
                                    >
                                        <p className="text-blueTheme text-lg font-semibold">
                                            Assumir tratamento em grupo
                                        </p>
                                        <IoCloseSharp
                                            onClick={handleCloseModal}
                                            className="text-blueTheme h-8 w-8 p-1 cursor-pointer"
                                        />
                                    </Dialog.Title>
                                    <Body>
                                        <p className="text-sm max-w-[600px] mb-16">
                                            Ao assumir o tratamento, todas as notificações do grupo ficarão em seu nome.
                                        </p>
                                        
                                        <ContentContainer className="mb-16">
                                            <p className="text-xs text-center font-medium">
                                                Responsável pela notificação
                                            </p>
                                            <div className="flex items-center justify-center mt-2">
                                                <img src={profile} alt="profile" width={24} />
                                                <span className="ml-3 text-roxo text-sm font-semibold">
                                                    {authenticationSelector?.dataJwt?.name}
                                                </span>
                                            </div>
                                            <p className="text-darkGray text-center">{profileStatus?.title}</p>
                                        </ContentContainer>
                                        
                                        <ButtonsContainer>
                                            <ButtonAction
                                                onClick={handleCloseModal}
                                                type="button"
                                                className="text-blueTheme text-sm"
                                            >
                                                Cancelar
                                            </ButtonAction>

                                            <ButtonAction
                                                onClick={handleAssumeTreatment}
                                                disabled={isLoading}
                                                className={`bg-blueTheme text-white text-sm flex items-center justify-center gap-2 ${
                                                    isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-90'
                                                }`}
                                            >
                                                {isLoading && <Spinner />}
                                                {isLoading ? 'Assumindo...' : 'Assumir tratamento em grupo'}
                                            </ButtonAction>
                                        </ButtonsContainer>
                                    </Body>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    )
}

export { GroupAssumeTreatmentModal } 